import { fetchAnalysisFilters, updateAnalysisView } from "@actions";
import { confirmModal } from "@actions/modals";
import { mapToSelect } from "@api/egm";
import { UIDateFormat, UITimeFormat } from "@helpers/dates";
import MultiSelect from "@shared/MultiSelect";
import moment from "moment";
import React, { useEffect } from "react";
import { connect } from "react-redux";
import { DateInput } from "semantic-ui-calendar-react";
import { Button, Dropdown, Form } from "semantic-ui-react";

const AnalysisFilters = (props) => {
  const {
    startDate,
    endDate,
    fromWeek,
    toWeek,
    categories,
    hours,
    weeks,
    selectedMeasures,
    dataGroups,
    daysOfWeek,
    totalBy,
    timeGroupBy,
    filteredTimeGroupBy,
    dataSource,
    selectedDataSource,
    selectedStartTime,
    selectedEndTime,
    selectedCategory,
    selectedTotalBy,
    selectedTimeGroupBy,
    selectedDayOfWeek,
    selectedMeasure,
    selectedDataGroup,
  } = props.analysis;
  const { measures } = dataSource.find((x) => x.id === selectedDataSource) || {
    measures: [],
  };
  const {
    displayWeeksOfYear,
    displayDayOfWeek,
    displayDataType,
    displayDataTypes,
    displayTotalOptions,
    displayHours,
  } = timeGroupBy.find((x) => x.id === selectedTimeGroupBy) || {
    displayWeeksOfYear: false,
    displayDataType: false,
    displayDayOfWeek: false,
    displayTotalOptions: false,
  };
  const { site, company, subcompany } = props.analysisFilters.filterSelected;

  const onChange = (e, { name, value }) => {
    props.updateAnalysisView({ [name]: value });
  };

  let selection = "All";
  if (company.id) selection = "CO";
  if (subcompany.id) selection = "SUB";
  if (site.id) selection = "SI";

  const processedGroups = dataGroups.filter((group) => {
    return (
      group.orgFilter.split(",").includes(selection) || group.orgFilter === ""
    );
  });

  const { updateAnalysisView } = props;

  useEffect(() => {
    const currentSelection = dataGroups.find((x) => x.id === selectedDataGroup);

    if (
      !currentSelection ||
      processedGroups.map((x) => x.id).includes(selectedDataGroup)
    ) {
      return;
    }

    const match = processedGroups.find((x) => x.name === currentSelection.name);

    if (!match) {
      return;
    }

    updateAnalysisView({ selectedDataGroup: match.id });
  }, [processedGroups, dataGroups, selectedDataGroup, updateAnalysisView]);

  useEffect(() => {
    if (!selectedDataSource || !measures || measures.length < 1) {
      return;
    }

    const measure = measures.find((x) => x.id === selectedMeasure);

    if (!measure) {
      updateAnalysisView({ selectedMeasure: measures[0].id });
    }
  }, [selectedDataSource, selectedMeasure, updateAnalysisView, measures]);

  const onDateChange = (name, value) => {
    props.updateAnalysisView({ [name]: moment(value, "DD-MM-YYYY HH:mm:ss") });
  };

  const onDataSourceChange = (e, { name, value }) => {
    let tmp = timeGroupBy.filter((x) =>
      x.displayForDataSources.includes(value),
    );
    if (tmp.length > 0) {
      if (tmp.filter((x) => x.id === selectedTimeGroupBy).length === 0) {
        props.updateAnalysisView({ selectedTimeGroupBy: tmp[0].id }); //reset id to first in the list
      }
    }

    props.updateAnalysisView({
      [name]: value,
      filteredTimeGroupBy: tmp,
    });
  };

  const onMultiSelectChange = (key, item) => {
    const selections = props.analysis[key];
    if (selections.includes(item)) {
      props.updateAnalysisView({ [key]: selections.filter((x) => x !== item) });
    } else {
      props.updateAnalysisView({ [key]: [...selections, item] });
    }
  };

  const disabled = !props.analysisFilters.hasSelection;

  return (
    <Form>
      <Form.Field>
        <label>Data Source</label>
        <Dropdown
          compact
          selection
          options={mapToSelect(dataSource)}
          disabled={disabled}
          value={selectedDataSource}
          name="selectedDataSource"
          onChange={onDataSourceChange}
        />
      </Form.Field>
      <Form.Field>
        <label>Data View</label>
        <Dropdown
          compact
          selection
          options={mapToSelect(filteredTimeGroupBy)}
          disabled={disabled}
          value={selectedTimeGroupBy}
          name="selectedTimeGroupBy"
          onChange={onChange}
        />
      </Form.Field>
      {!displayWeeksOfYear && (
        <Form.Field>
          <DateInput
            label="From"
            name="startDate"
            disabled={disabled}
            iconPosition="right"
            value={startDate.format(UIDateFormat)}
            onChange={(e, { value }) =>
              onDateChange(
                "startDate",
                displayHours
                  ? `${value} ${startDate.format(UITimeFormat)}`
                  : `${value} 00:00:00`,
              )
            }
            animation="fade"
            popupPosition="bottom center"
            closable
            inlineLabel={false}
            autoComplete="off"
            dateFormat={UIDateFormat}
          />
        </Form.Field>
      )}
      {!displayWeeksOfYear && (
        <Form.Field>
          <DateInput
            label="To"
            inlineLabel={false}
            name="endDate"
            disabled={disabled}
            value={endDate.format(UIDateFormat)}
            onChange={(e, { value }) =>
              onDateChange(
                "endDate",
                displayHours
                  ? `${value} ${endDate.format(UITimeFormat)}`
                  : `${value} 23:59:59`,
              )
            }
            iconPosition="right"
            animation="fade"
            popupPosition="bottom center"
            closable
            autoComplete="off"
            dateFormat={UIDateFormat}
          />
        </Form.Field>
      )}
      {displayWeeksOfYear && (
        <Form.Field>
          <label>From</label>
          <Dropdown
            selection
            compact
            options={mapToSelect(weeks)}
            disabled={disabled}
            name="fromWeek"
            value={fromWeek}
            onChange={onChange}
          />
        </Form.Field>
      )}
      {displayWeeksOfYear && (
        <Form.Field>
          <label>To</label>
          <Dropdown
            selection
            compact
            options={mapToSelect(weeks)}
            disabled={disabled}
            name="toWeek"
            value={toWeek}
            onChange={onChange}
          />
        </Form.Field>
      )}
      {displayHours && (
        <Form.Field>
          <label>Start Hour</label>
          <Dropdown
            selection
            compact
            options={mapToSelect(hours)}
            disabled={disabled}
            value={selectedStartTime}
            name="selectedStartTime"
            onChange={onChange}
          />
        </Form.Field>
      )}
      {displayHours && (
        <Form.Field>
          <label>End Hour</label>
          <Dropdown
            selection
            compact
            options={mapToSelect(hours)}
            disabled={disabled}
            value={selectedEndTime}
            name="selectedEndTime"
            onChange={onChange}
          />
        </Form.Field>
      )}
      {displayDataType && measures.length > 0 && (
        <Form.Field>
          <label>Data Type</label>
          <Dropdown
            selection
            compact
            options={mapToSelect(measures)}
            disabled={disabled}
            value={selectedMeasure}
            name="selectedMeasure"
            onChange={onChange}
          />
        </Form.Field>
      )}

      {displayDataTypes && (
        <Form.Field>
          <label>{selectedTimeGroupBy === 4 ? "Graph" : "Data Types"}</label>
          <MultiSelect
            compact
            disabled={disabled}
            options={measures}
            selections={selectedMeasures}
            onChange={(id) => onMultiSelectChange("selectedMeasures", id)}
          />
        </Form.Field>
      )}
      <Form.Field>
        <label>Group By</label>
        <Dropdown
          selection
          compact
          options={mapToSelect(processedGroups)}
          disabled={disabled}
          value={selectedDataGroup}
          name="selectedDataGroup"
          onChange={onChange}
        />
      </Form.Field>
      {displayTotalOptions && (
        <Form.Field>
          <label>Aggregation</label>
          <Dropdown
            selection
            compact
            name="selectedTotalBy"
            options={mapToSelect(totalBy)}
            value={selectedTotalBy}
            onChange={onChange}
          />
        </Form.Field>
      )}
      {displayDayOfWeek && (
        <Form.Field>
          <label>Days Of The Week</label>
          <MultiSelect
            compact
            disabled={disabled}
            options={daysOfWeek.filter((x) => x.name !== "All")}
            selections={selectedDayOfWeek}
            onChange={(id) => onMultiSelectChange("selectedDayOfWeek", id)}
          />
        </Form.Field>
      )}
      <Form.Field>
        <label>Category</label>
        <MultiSelect
          disabled={disabled}
          options={categories.filter((x) => x.name !== "All")}
          selections={selectedCategory}
          compact
          onChange={(id) => onMultiSelectChange("selectedCategory", id)}
        />
      </Form.Field>
      <Button
        fluid
        compact
        icon="refresh"
        content="Process"
        positive
        onClick={props.onRefresh}
        disabled={disabled}
      />
    </Form>
  );
};

const mapStateToProps = (state) => {
  return {
    analysis: state.analysisView,
    analysisFilters: state.analysisFilters,
  };
};

export default connect(mapStateToProps, {
  updateAnalysisView,
  fetchAnalysisFilters,
  confirmModal,
})(AnalysisFilters);
